// Auto-extracted from Profile.jsx
import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Btn } from "../../components/Btn";
import { Eyebrow } from "../../components/Eyebrow";
import { Icon } from "../../components/Icon";
import { Skeleton } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { ProductCard } from "../../components/ProductCard";
import { supabase } from "../../lib/supabase";
import { fmtK } from "../../lib/money";
import { useWishlist } from "../../store/wishlistStore";
import { getAllLocations } from "../../data/zambianLocations";
import { timeAgo } from "../../lib/dateUtils";
import { useCategories } from "../../hooks/useCategories";
import { BecomeSellerModal } from "../../components/BecomeSellerModal";
import { PhoneVerificationModal } from "../../components/PhoneVerificationModal";
import { DeleteAccountModal } from "../../components/DeleteAccountModal";
import { MFASetup } from "../MFASetup";
import { Field, SecurityRow, InfoRow, inputStyle } from "./shared";
import { DeleteAccountRow, PhoneVerifyRow } from "./uiHelpers";

// Mirrors mobile CPrivacy.tsx content so both clients show the same information.
const DATA_ITEMS = [
  { label: "Name & email", desc: "Used for your account and order receipts" },
  { label: "Phone number", desc: "Used for mobile money payments" },
  { label: "Order history", desc: "Stored for returns, support, and receipts" },
  { label: "Messages", desc: "Conversations with sellers — encrypted in transit" },
  { label: "Location", desc: "Used to show nearby products (never shared)" },
];

const USER_RIGHTS = [
  { label: "Access your data", desc: "View all data we store about you" },
  { label: "Update your info", desc: "Edit profile, addresses, payment methods" },
  { label: "Delete your account", desc: "Permanently delete all your data" },
  { label: "Opt out of tracking", desc: "Disable activity tracking in Settings" },
];

export function SecurityPanel({ user, onUserUpdated }) {
  const [pwd, setPwd] = useState({ next: "", confirm: "" });
  const [state, setState] = useState({ saving: false, msg: null, error: null });
  const [blocked, setBlocked] = useState([]);
  const [blockedLoading, setBlockedLoading] = useState(true);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  // MFA state — tracks whether the user already has a verified TOTP factor and
  // whether the inline setup wizard is open. Refetched after enrollment so the
  // UI flips to the "enabled" state immediately.
  const [mfaFactorId, setMfaFactorId] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(true);
  const [mfaSetupOpen, setMfaSetupOpen] = useState(false);
  const [mfaActionState, setMfaActionState] = useState({ saving: false, error: null });

  const fetchMfaStatus = async () => {
    setMfaLoading(true);
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) {
        setMfaFactorId(null);
        return;
      }
      const verified = data?.totp?.find((f) => f.status === "verified");
      setMfaFactorId(verified?.id || null);
    } finally {
      setMfaLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    fetchMfaStatus();
  }, [user?.id]);

  const disableMfa = async () => {
    if (!mfaFactorId) return;
    if (!window.confirm("Disable two-factor authentication? You'll only need your password to sign in.")) return;
    setMfaActionState({ saving: true, error: null });
    const { error } = await supabase.auth.mfa.unenroll({ factorId: mfaFactorId });
    if (error) {
      setMfaActionState({ saving: false, error: error.message || "Could not disable 2FA." });
      return;
    }
    setMfaActionState({ saving: false, error: null });
    await fetchMfaStatus();
  };

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    supabase
      .from("blocks")
      .select("blocked_id, users!blocks_blocked_id_fkey(name)")
      .eq("blocker_id", user.id)
      .limit(100)
      .then(({ data }) => {
        if (cancelled) return;
        setBlocked(data || []);
        setBlockedLoading(false);
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  async function change() {
    setState({ saving: true, msg: null, error: null });
    if (pwd.next.length < 8) return setState({ saving: false, msg: null, error: "Password must be at least 8 characters." });
    if (pwd.next !== pwd.confirm) return setState({ saving: false, msg: null, error: "Passwords do not match." });
    const { error } = await supabase.auth.updateUser({ password: pwd.next });
    if (error) return setState({ saving: false, msg: null, error: error.message });
    setPwd({ next: "", confirm: "" });
    setState({ saving: false, msg: "Password updated.", error: null });
    setTimeout(() => setState((s) => ({ ...s, msg: null })), 3000);
  }

  async function unblock(blockedId) {
    const { error } = await supabase
      .from("blocks")
      .delete()
      .eq("blocker_id", user.id)
      .eq("blocked_id", blockedId);
    if (!error) setBlocked((prev) => prev.filter((b) => b.blocked_id !== blockedId));
  }

  // When the setup wizard is open, render it in place of the panel content.
  // Stays inside the same Profile container so the sidebar nav remains visible.
  if (mfaSetupOpen) {
    return (
      <MFASetup
        onClose={() => setMfaSetupOpen(false)}
        onComplete={async () => {
          setMfaSetupOpen(false);
          await fetchMfaStatus();
        }}
      />
    );
  }

  return (
    <div style={{ display: "grid", gap: 14 }}>
      {/* ── Two-factor authentication — opt-in for sellers + customers ── */}
      <Card pad={22}>
        <Eyebrow>Two-factor authentication</Eyebrow>
        <div style={{ marginTop: 10 }}>
          {mfaLoading ? (
            <Skeleton h={56} />
          ) : mfaFactorId ? (
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <span
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 12,
                  background: C.greenBg,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon name="shield" size={16} color={C.hunter} />
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: C.charcoal }}>
                  2FA is enabled
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                  Your account requires a code from your authenticator app at sign-in.
                </div>
              </div>
              <Btn kind="ghost" size="sm" onClick={disableMfa} disabled={mfaActionState.saving}>
                {mfaActionState.saving ? "Disabling…" : "Disable"}
              </Btn>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <span
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 12,
                  background: C.cream,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon name="shield" size={16} color={C.slate} />
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: C.charcoal }}>
                  Add an extra layer of security
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                  Require a 6-digit code from an authenticator app at sign-in.
                </div>
              </div>
              <Btn onClick={() => setMfaSetupOpen(true)}>Set up 2FA</Btn>
            </div>
          )}
          {mfaActionState.error && (
            <div
              style={{
                marginTop: 10,
                padding: "8px 12px",
                background: C.redBg,
                border: `1px solid ${C.redBorder}`,
                borderRadius: 10,
                fontSize: 12.5,
                color: C.red,
                fontWeight: 600,
              }}
            >
              {mfaActionState.error}
            </div>
          )}
        </div>
      </Card>

      {/* Change password — desktop-only convenience */}
      <Card pad={22}>
        <Eyebrow>Change password</Eyebrow>
        <div style={{ display: "grid", gap: 12, marginTop: 10 }}>
          <Field label="New password"><input type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} style={inputStyle} autoComplete="new-password" /></Field>
          <Field label="Confirm new password"><input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} style={inputStyle} autoComplete="new-password" /></Field>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <Btn onClick={change} disabled={state.saving || !pwd.next || !pwd.confirm}>{state.saving ? "Updating…" : "Update password"}</Btn>
            {state.msg && <span style={{ fontSize: 12, color: C.greenDk, fontWeight: 700 }}>{state.msg}</span>}
            {state.error && <span style={{ fontSize: 12, color: C.red, fontWeight: 700 }}>{state.error}</span>}
          </div>
        </div>
      </Card>

      {/* Legal links */}
      <Card pad={0}>
        <a
          href="https://kapio-apps.pages.dev/privacy.html"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: "flex", alignItems: "center", gap: 12, padding: 14, textDecoration: "none", color: "inherit", borderBottom: `1px solid ${C.cream}` }}
        >
          <span style={{ width: 36, height: 36, borderRadius: 10, background: C.greenBg, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="shield" size={16} color={C.hunter} />
          </span>
          <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: C.charcoal }}>Privacy Policy</span>
          <Icon name="arrow-right" size={14} color={C.muted} />
        </a>
        <a
          href="https://kapio-apps.pages.dev/terms.html"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: "flex", alignItems: "center", gap: 12, padding: 14, textDecoration: "none", color: "inherit" }}
        >
          <span style={{ width: 36, height: 36, borderRadius: 10, background: C.cream, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="file-text" size={16} color={C.slate} />
          </span>
          <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: C.charcoal }}>Terms of Service</span>
          <Icon name="arrow-right" size={14} color={C.muted} />
        </a>
      </Card>

      {/* Data we collect */}
      <Card pad={0}>
        <div style={{ padding: "16px 22px", borderBottom: `1px solid ${C.cream}` }}>
          <Eyebrow>Data we collect</Eyebrow>
        </div>
        {DATA_ITEMS.map((d, i) => (
          <div key={d.label} style={{ padding: "12px 22px", borderBottom: i < DATA_ITEMS.length - 1 ? `1px solid ${C.cream}` : "none" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.charcoal }}>{d.label}</div>
            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>{d.desc}</div>
          </div>
        ))}
      </Card>

      {/* Your rights */}
      <Card pad={0}>
        <div style={{ padding: "16px 22px", borderBottom: `1px solid ${C.cream}` }}>
          <Eyebrow>Your rights</Eyebrow>
        </div>
        {USER_RIGHTS.map((r, i) => (
          <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 22px", borderBottom: i < USER_RIGHTS.length - 1 ? `1px solid ${C.cream}` : "none" }}>
            <Icon name="shield" size={14} color={C.hunter} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.charcoal }}>{r.label}</div>
              <div style={{ fontSize: 11.5, color: C.muted, marginTop: 1 }}>{r.desc}</div>
            </div>
          </div>
        ))}
      </Card>

      {/* Blocked users — live from Supabase `blocks` table */}
      <Card pad={0}>
        <div style={{ padding: "16px 22px", borderBottom: `1px solid ${C.cream}` }}>
          <Eyebrow>Blocked users</Eyebrow>
        </div>
        {blockedLoading ? (
          <div style={{ padding: 20 }}><Skeleton h={40} /></div>
        ) : blocked.length === 0 ? (
          <div style={{ padding: 20, fontSize: 12.5, color: C.muted, textAlign: "center" }}>No blocked users</div>
        ) : (
          blocked.map((b, i) => {
            const name = (Array.isArray(b.users) ? b.users[0]?.name : b.users?.name) || "User";
            return (
              <div key={b.blocked_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 22px", borderBottom: i < blocked.length - 1 ? `1px solid ${C.cream}` : "none" }}>
                <Icon name="user" size={14} color={C.muted} />
                <span style={{ flex: 1, fontSize: 13, fontWeight: 700, color: C.charcoal }}>{name}</span>
                <button
                  onClick={() => unblock(b.blocked_id)}
                  style={{ all: "unset", cursor: "pointer", fontSize: 12, fontWeight: 700, color: C.hunter }}
                >
                  Unblock
                </button>
              </div>
            );
          })
        )}
      </Card>

      {/* Account basics — email + phone verification + delete pointer */}
      <Card pad={22}>
        <Eyebrow>Account</Eyebrow>
        <div style={{ marginTop: 12, display: "grid", gap: 14 }}>
          <SecurityRow icon="mail" title="Email" body={user?.email} />
          <PhoneVerifyRow
            user={user}
            onOpenVerify={() => setPhoneOpen(true)}
          />
          <DeleteAccountRow onOpenDelete={() => setDeleteOpen(true)} />
        </div>
      </Card>

      <PhoneVerificationModal
        open={phoneOpen}
        onClose={() => setPhoneOpen(false)}
        userId={user?.id}
        initialPhone={user?.phone || ""}
        onSuccess={async () => {
          setPhoneOpen(false);
          await onUserUpdated?.();
        }}
      />

      <DeleteAccountModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        user={user}
      />
    </div>
  );
}

// Interactive delete-account row. Sits alongside phone verification in the
// Account card. The heavy lifting (eligibility check, password re-auth, Edge
// Function call) lives in DeleteAccountModal — this row is purely the entry
